import { useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  DragNDrop,
  Icon,
  Input,
  Modal,
  Select,
  Text,
} from '@fairfashion/design-system'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'

import { ConfirmModalContext } from '../ConfirmModal'

import { DocumentForm, DocumentsProps } from './documents.types'
import { getIconName } from './documents.utils'

const categoryOptions = [
  { value: 'certificate', label: 'Certificado' },
  { value: 'others', label: 'Outros' },
]

export const Documents = ({
  documents,
  expiredDocuments,
  onAddDocument,
  onDeleteDocument,
}: DocumentsProps) => {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string>()

  const getFileName = (url: string) => {
    const lastPart = url.split('/').at(-1)
    return lastPart?.split('?').at(0)
  }
  const { register, control, handleSubmit, reset } = useForm<DocumentForm>()

  const { confirmModal } = useContext(ConfirmModalContext)

  const submitDocument = async (data: DocumentForm) => {
    const originalFileName = data.file?.name

    const body = {
      name:
        data.name && data?.name?.length
          ? data.name.replace(/[^a-zA-Z0-9]/g, '')
          : originalFileName,
      file: data.file,
      category: data.category,
      expiration:
        data.expiration &&
        moment(data.expiration, 'DD/MM/YYYY').toLocaleString(),
    }

    if (onAddDocument) {
      try {
        await onAddDocument(body)
        setOpen(false)
      } catch (error: any) {
        setError(error?.error?.message)
      }
    }
  }

  useEffect(() => {
    if (open) {
      reset({ name: '', file: undefined, expiration: '', category: '' })
      setError('')
    }
  }, [open])

  const handleDelete = async (id: string) => {
    if (
      await confirmModal(
        'Excluir Documento',
        'Tem certeza que deseja excluir este documento?',
      )
    ) {
      onDeleteDocument && onDeleteDocument(id)
    }
  }

  return (
    <Box width="100%" direction="column" gap={3}>
      {onAddDocument && (
        <Box width="100%" marginTop={3} direction="column" gap={3}>
          <Box
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Text size="large">Documentos</Text>
            <Button onClick={() => setOpen(true)} padding="10px 14px">
              <Box gap={2} alignItems="center" minWidth="230px">
                <Icon name="add" color="white" size={20} />
                Adicionar Documento
              </Box>
            </Button>
          </Box>
          <Box height="1px" width="100%" backgroundColor="grey_4" />
        </Box>
      )}

      <Box width="100%" direction="column" gap={2}>
        {documents.map((document) => (
          <Box
            key={document.id}
            width="100%"
            gap={3}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box gap={1} alignItems="center">
              <Icon name={getIconName(document.extension)} />
              <a href={document.file} target="_blank" rel="noreferrer">
                <Text size="small">{getFileName(document.file)}</Text>
              </a>
            </Box>
            <Box gap={2} alignItems="center">
              <Text size="small" color="grey_3">
                {document.expiration
                  ? `Expira em: ${new Date(document.expiration)?.toLocaleDateString()}`
                  : 'Sem data de expiração '}
              </Text>
              {onDeleteDocument && (
                <Button
                  variant="text"
                  padding="0"
                  onClick={() => handleDelete(document.id)}
                >
                  <Icon name="delete_bin" color="grey_3" size={20} />
                </Button>
              )}
            </Box>
          </Box>
        ))}
        {expiredDocuments && expiredDocuments.length > 0 && (
          <>
            <Box height="1px" width="100%" backgroundColor="grey_4" />

            {expiredDocuments.map((document) => (
              <Box
                key={document.id}
                width="100%"
                gap={3}
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Box gap={1} alignItems="center">
                  <Icon name={getIconName(document.type)} />
                  <Text size="small">{getFileName(document.file)}</Text>
                </Box>
                <Box>
                  <Text size="small" color="error">
                    {`Expirado em: ${new Date(document.expiration)?.toLocaleDateString()}`}
                  </Text>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Adicionar Documento"
        width="800px"
        maxWidth="90%"
      >
        <form onSubmit={handleSubmit(submitDocument)}>
          <Box width="100%" direction="column" gap={3} marginTop={2}>
            <Box width="100%">
              <Controller
                name="file"
                control={control}
                render={({ field }) => (
                  <DragNDrop
                    onChange={(files) => field.onChange(files?.[0])}
                    fileTypeError="Tipo de arquivo inválido"
                  />
                )}
              />
            </Box>
            <Box width="100%" gap={2} flexWrap="wrap">
              <Box minWidth="200px" flex={1}>
                <Input {...register('name')} label="Nome do Documento" />
              </Box>
              <Box minWidth="200px" flex={1}>
                <Input
                  {...register('expiration')}
                  label="Validade"
                  mask="00/00/0000"
                />
              </Box>
              <Box minWidth="200px" flex={2}>
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <Select
                      label="Categoria"
                      placeholder="Categoria"
                      options={categoryOptions}
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box
              width="100%"
              justifyContent="space-between"
              marginTop={1}
              alignItems="center"
            >
              <Box>
                {error && (
                  <Text size="small" color="error">
                    {error}
                  </Text>
                )}
              </Box>
              <Box gap={2}>
                <Button variant="outlined" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Adicionar</Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Modal>
    </Box>
  )
}
